import { type ToOptions, useRouter } from "@tanstack/react-router";
import React from "react";

import type { Selected } from "@/lib/types";
import { useTinyKeys } from "@/lib/useTinyKeys";

/**
 * adds a global keyboard shortcut to go back to the previous page
 * if the current page has a `from` state property
 */
export function useInitializeNavStack(deps: unknown[] = []) {
  const router = useRouter();
  const state = router.history.location.state;

  useTinyKeys(
    {
      Escape: () => {
        if (state?.href) {
          history.back();
        }
      },
    },
    [state?.href, ...deps],
  );
}

/**
 * This stores the selected item for a given route/pathname
 * useCaptureRouteState will reset it when the state no
 * longer indicates the `from` state property
 */
const globalCache = new Map<string, Selected>();

/**
 * get the selected item for the current route
 */
function getSelected() {
  const pathname = location.pathname;
  return globalCache.get(pathname);
}

/**
 * This hook will listen to route changes and store the selected
 * item for the current route
 */
export function useCaptureRouteState() {
  const router = useRouter();

  React.useEffect(() => {
    const unsub = router.subscribe("onLoad", (event) => {
      const from = event.fromLocation;
      const to = event.toLocation;

      const toFrom = to.state.href?.to;

      // if there's no from state on the new route and the
      // pathname is different from the previous route's from (i.e. going back)
      // this indicates the user is navigating to a new route and should
      // create a new "stack".
      if (!toFrom && to.pathname !== from?.pathname) {
        globalCache.clear();
      }

      // if there's a from state and the new route has a selected item
      if (toFrom && from?.pathname && to.state.selected) {
        globalCache.set(from.pathname, event.toLocation.state.selected);
      }
    });
    return () => {
      unsub();
    };
  }, [router]);
}

/**
 * This hook will return the selected item for the current route
 * and a reset function to clear it
 */
export function useNavSelected() {
  const reset = React.useCallback(() => {
    const pathname = location.pathname;
    globalCache.delete(pathname);
  }, []);

  return { selected: getSelected(), reset };
}

type NavStackContextValue = {
  label: string;
  href: ToOptions;
};

const NavStackContext = React.createContext<NavStackContextValue | null>(null);

export function NavStackProvider({
  children,
  label,
  href,
}: {
  children: React.ReactNode;
  label: string;
  href: ToOptions;
}) {
  return (
    <NavStackContext.Provider value={{ label, href }}>
      {children}
    </NavStackContext.Provider>
  );
}

function useNavStack() {
  const context = React.useContext(NavStackContext);
  if (!context) {
    throw new Error("useNavStack must be used within a NavStackProvider");
  }
  return context;
}

export function useNavLocationState(defaultState?: {
  href: ToOptions;
  label: string;
}) {
  const router = useRouter();
  const stackState = useNavStack();

  const locationState = router.state.location.state;

  return {
    href: locationState?.href ?? defaultState?.href ?? stackState.href,
    label: locationState?.label ?? defaultState?.label ?? stackState.label,
  };
}
